import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { useToast } from '@/hooks/use-toast';

export interface VendorInvitation {
  id: string;
  rfq_id: string;
  vendor_user_id: string;
  invited_by_user_id: string;
  invitation_status: 'invited' | 'viewed' | 'declined' | 'bid_submitted' | 'expired';
  invited_at: string;
  viewed_at?: string;
  responded_at?: string;
  expires_at: string;
  invitation_token: string;
  invitation_notes?: string;
  reminder_count: number;
  last_reminder_sent_at?: string;
  vendor_profile?: any; // Will be populated via join
  rfq?: any; // Will be populated for vendor invitations
}

export interface InviteVendorsRequest {
  rfq_id: string;
  vendor_user_ids: string[];
  invitation_notes?: string;
}

export const useRFQVendorInvitations = (rfqId?: string) => {
  const [invitations, setInvitations] = useState<VendorInvitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchInvitations = async () => {
    if (!rfqId) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('rfq_vendor_invitations')
        .select(`
          *,
          vendor_profile:vendor_profiles_enhanced!vendor_user_id(
            id,
            user_id,
            business_name,
            headquarters_city,
            state,
            country,
            is_verified,
            verification_level,
            average_rating
          )
        `)
        .eq('rfq_id', rfqId)
        .order('invited_at', { ascending: false });

      if (error) throw error;

      setInvitations(data as VendorInvitation[] || []);
    } catch (err) {
      console.error('Error fetching RFQ vendor invitations:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch invitations');
    } finally {
      setLoading(false);
    }
  };

  const inviteVendors = async ({ rfq_id, vendor_user_ids, invitation_notes }: InviteVendorsRequest) => {
    const MAX_RETRIES = 3;
    let attempt = 0;
    let lastError: any;

    while (attempt < MAX_RETRIES) {
      try {
        setLoading(true);
        setError(null);

        // Pre-generate unique invitation tokens to prevent race conditions
        const generateInvitationToken = () => {
          const timestamp = Date.now();
          const random = Math.random().toString(36).substring(2, 10).toUpperCase();
          return `INV-${timestamp}-${random}`;
        };

        // Create invitation records with pre-generated tokens
        const invitationData = vendor_user_ids.map(vendor_user_id => ({
          rfq_id,
          vendor_user_id,
          invited_by_user_id: '', // Will be set by RLS policy
          invitation_notes: invitation_notes || null,
          invitation_token: generateInvitationToken(),
          expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
        }));

        const { data, error } = await supabase
          .from('rfq_vendor_invitations')
          .insert(invitationData)
          .select();

        if (error) {
          // Check for duplicate key constraint violation
          if (error.code === '23505' && error.message?.includes('invitation_token')) {
            lastError = error;
            attempt++;
            
            if (attempt < MAX_RETRIES) {
              // Wait before retry (exponential backoff: 100ms, 200ms, 400ms)
              await new Promise(resolve => setTimeout(resolve, 100 * Math.pow(2, attempt - 1)));
              console.log(`Retrying invitation insert (attempt ${attempt + 1}/${MAX_RETRIES})...`);
              continue;
            }
          }
          throw error;
        }

        // Send notification emails via edge function
        try {
          await invokeEdgeFunction('rfq-invitation-notify', {
            body: {
              rfqId: rfq_id,
              vendorUserIds: vendor_user_ids
            },
            callerOperation: 'useRFQVendorInvitations.inviteVendors',
          });
        } catch (emailError) {
          console.warn('Failed to send invitation emails:', emailError);
          // Don't fail the entire operation if email fails
        }

        // Get RFQ and job details for notification
        const { data: rfqDetails } = await supabase
          .from('rfqs')
          .select('job_id, jobs!inner(public_id, title)')
          .eq('id', rfq_id)
          .single();

        if (rfqDetails) {
          // Notify via edge function
          await invokeEdgeFunction('rfq-invitation-notify', {
            body: { rfqId: rfq_id, vendorUserIds: vendor_user_ids },
            callerOperation: 'useRFQVendorInvitations.inviteVendors.notify',
          });
        }

        toast({
          title: 'Vendors Invited',
          description: `Successfully invited ${vendor_user_ids.length} vendor(s) to the RFQ`,
        });

        // Refresh invitations list
        fetchInvitations();

        return data;
      } catch (err) {
        // If it's not a retryable error or we've exhausted retries, throw immediately
        if (!lastError || attempt >= MAX_RETRIES) {
          console.error('Error inviting vendors:', err);
          const errorMessage = err instanceof Error ? err.message : 'Failed to invite vendors';
          setError(errorMessage);
          toast({
            title: 'Error Inviting Vendors',
            description: errorMessage,
            variant: 'destructive',
          });
          throw err;
        }
        // Otherwise, continue to next retry attempt
      } finally {
        setLoading(false);
      }
    }

    // If we've exhausted all retries
    const errorMessage = 'Failed to invite vendors after multiple attempts';
    setError(errorMessage);
    toast({
      title: 'Error Inviting Vendors',
      description: errorMessage,
      variant: 'destructive',
    });
    throw lastError || new Error(errorMessage);
  };

  const updateInvitationStatus = async (invitationId: string, status: VendorInvitation['invitation_status']) => {
    try {
      const updateData: any = {
        invitation_status: status,
        responded_at: new Date().toISOString(),
      };

      if (status === 'viewed') {
        updateData.viewed_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('rfq_vendor_invitations')
        .update(updateData)
        .eq('id', invitationId);

      if (error) throw error;

      // Update local state
      setInvitations(prev => prev.map(inv => 
        inv.id === invitationId 
          ? { ...inv, ...updateData }
          : inv
      ));

      return true;
    } catch (err) {
      console.error('Error updating invitation status:', err);
      setError(err instanceof Error ? err.message : 'Failed to update invitation status');
      return false;
    }
  };

  const resendInvitation = async (invitationId: string) => {
    try {
      setLoading(true);

      const invitation = invitations.find(inv => inv.id === invitationId);
      if (!invitation) throw new Error('Invitation not found');

      // Update reminder count
      const { error } = await supabase
        .from('rfq_vendor_invitations')
        .update({
          reminder_count: invitation.reminder_count + 1,
          last_reminder_sent_at: new Date().toISOString(),
        })
        .eq('id', invitationId);

      if (error) throw error;

      // Send reminder email
      await invokeEdgeFunction('send-rfq-invitation', {
        body: {
          rfq_id: invitation.rfq_id,
          vendor_user_ids: [invitation.vendor_user_id],
          is_reminder: true,
        },
        callerOperation: 'useRFQVendorInvitations.resendInvitation',
      });

      toast({
        title: 'Reminder Sent',
        description: 'Invitation reminder has been sent to the vendor',
      });

      fetchInvitations();
    } catch (err) {
      console.error('Error resending invitation:', err);
      toast({
        title: 'Error Sending Reminder',
        description: err instanceof Error ? err.message : 'Failed to send reminder',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const removeInvitation = async (invitationId: string) => {
    try {
      const { error } = await supabase
        .from('rfq_vendor_invitations')
        .delete()
        .eq('id', invitationId);

      if (error) throw error;

      setInvitations(prev => prev.filter(inv => inv.id !== invitationId));

      toast({
        title: 'Invitation Removed',
        description: 'Vendor invitation has been removed',
      });
    } catch (err) {
      console.error('Error removing invitation:', err);
      toast({
        title: 'Error Removing Invitation',
        description: err instanceof Error ? err.message : 'Failed to remove invitation',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    if (rfqId) {
      fetchInvitations();
    }
  }, [rfqId]);

  return {
    invitations,
    loading,
    error,
    inviteVendors,
    updateInvitationStatus,
    resendInvitation,
    removeInvitation,
    refetch: fetchInvitations,
  };
};

// Hook for vendors to view their invitations
export const useVendorInvitations = () => {
  const [invitations, setInvitations] = useState<VendorInvitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMyInvitations = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('rfq_vendor_invitations')
        .select(`
          *,
          rfq:rfqs(
            id,
            public_id,
            status,
            closes_at,
            visibility_type,
            job:jobs(
              id,
              title,
              description,
              category,
              due_date,
              organization:organizations(name)
            )
          )
        `)
        .eq('vendor_user_id', (await supabase.auth.getUser()).data.user?.id)
        .neq('invitation_status', 'expired')
        .order('invited_at', { ascending: false });

      if (error) throw error;

      setInvitations(data as VendorInvitation[] || []);
    } catch (err) {
      console.error('Error fetching vendor invitations:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch invitations');
    } finally {
      setLoading(false);
    }
  };

  const respondToInvitation = async (invitationId: string, response: 'viewed' | 'declined') => {
    try {
      const { error } = await supabase
        .from('rfq_vendor_invitations')
        .update({
          invitation_status: response,
          responded_at: new Date().toISOString(),
          viewed_at: response === 'viewed' ? new Date().toISOString() : undefined,
        })
        .eq('id', invitationId);

      if (error) throw error;

      // Update local state
      setInvitations(prev => prev.map(inv => 
        inv.id === invitationId 
          ? { 
              ...inv, 
              invitation_status: response, 
              responded_at: new Date().toISOString(),
              viewed_at: response === 'viewed' ? new Date().toISOString() : inv.viewed_at 
            }
          : inv
      ));

      return true;
    } catch (err) {
      console.error('Error responding to invitation:', err);
      setError(err instanceof Error ? err.message : 'Failed to respond to invitation');
      return false;
    }
  };

  useEffect(() => {
    fetchMyInvitations();
  }, []);

  return {
    invitations,
    loading,
    error,
    respondToInvitation,
    refetch: fetchMyInvitations,
  };
};